import React, { useState, useEffect } from "react";
import {
  Typography,
  Paper,
  Grid,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  MenuItem,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  StyledAutocomplete,
  StyledSelect,
  StyledTableCellHistory,
  StyledTableRowHistory,
  StyledTextField,
  theme,
} from "../StyledComponents";
import { ThemeProvider } from "@mui/material/styles";
import axios from "axios";

const unitOptions = ["mg/dL", "g/dL", "mmol/L", "cells/mcL", "µg/L", "L", "%"];

const LoincAccordionPanelInput = ({ data, setData }) => {
  const [searchResults, setSearchResults] = useState([]);
  const [selectedPanel, setSelectedPanel] = useState("");
  const [expandedPanel, setExpandedPanel] = useState(null);
  const [searchText, setSearchText] = useState("");
  const panelEntries = Array.isArray(data) ? data : [];
  const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;
  // 🔹 recursive flatten helper
  // const flattenTests = (items, panelName, seen = new Set()) => {
  //   let flat = [];
  //   for (const t of items) {
  //     if (!t.LOINC_NUMBER || seen.has(t.LOINC_NUMBER)) continue;
  //     seen.add(t.LOINC_NUMBER);

  //     flat.push({
  //       id: `${t.LOINC_NUMBER}_${panelName}`,
  //       test: t.Name || "-",
  //       code: t.LOINC_NUMBER || "-",
  //       // description: t.SHORTNAME || t.LONG_COMMON_NAME || "-",
  //       value: "",
  //       unit: t.Example_UCUM_UNITS || "",
  //     });

  //     if (Array.isArray(t.Children)) {
  //       flat = flat.concat(flattenTests(t.Children, panelName, seen));
  //     }
  //     if (Array.isArray(t.grandChildren)) {
  //       flat = flat.concat(flattenTests(t.grandChildren, panelName, seen));
  //     }
  //   }
  //   return flat;
  // };

  // skip top-level test, only include children & grandchildren
  const flattenTests = (items, panelName, seen = new Set()) => {
    let flat = [];
    for (const t of items) {
      // ⛔ skip the parent panel row (just recurse its children)
      if (!t.Children && !t.grandChildren) {
        if (!t.LOINC_NUMBER || seen.has(t.LOINC_NUMBER)) continue;
        seen.add(t.LOINC_NUMBER);

        flat.push({
          id: `${t.LOINC_NUMBER}_${panelName}`,
          test: t.Name || "-",
          code: t.LOINC_NUMBER || "-",
          // description: t.SHORTNAME || t.LONG_COMMON_NAME || "-",
          value: "",
          unit: t.Example_UCUM_UNITS || "",
        });
      }

      if (Array.isArray(t.Children)) {
        flat = flat.concat(flattenTests(t.Children, panelName, seen));
      }
      if (Array.isArray(t.grandChildren)) {
        flat = flat.concat(flattenTests(t.grandChildren, panelName, seen));
      }
    }
    return flat;
  };

  // 🔹 fetch panels dynamically from loinc/search
  // const handlePanelSearch = async (value) => {
  //   if (!value || value.length < 2) {
  //     setSearchResults([]);
  //     return;
  //   }
  //   try {
  //     const res = await axios.get(
  //       `http://3.6.121.41:8081/loincserv/v1/search`,
  //       {
  //         params: {
  //           class: "ALL",
  //           classType: "ALL",
  //           component: "ALL",
  //           exampleUnits: "ALL",
  //           limit: 20,
  //           method: "ALL",
  //           panelType: "PANEL",
  //           property: "ALL",
  //           scale: "ALL",
  //           sortByRank: false,
  //           status: "ACTIVE",
  //           system: "ALL",
  //           term: value,
  //           timing: "ALL",
  //         },
  //       }
  //     );

  //     const results = res.data.map((item) => ({
  //       panel_name:
  //         item.DISPLAY_NAME ||
  //         item.LONG_COMMON_NAME ||
  //         item.COMPONENT ||
  //         item.LOINC_NUMBER,
  //       loinc_num: item.LOINC_NUMBER,
  //     }));
  //     setSearchResults(results);
  //   } catch (err) {
  //     console.error("Error fetching panels:", err);
  //   }
  // };

  const handlePanelSearch = async (value) => {
    if (!value || value.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_ROUTE_URL}loinc/search/`,
        {
          class: "ALL",
          classType: "ALL",
          component: "ALL",
          exampleUnits: "ALL",
          limit: 10000, // increased limit
          method: "ALL",
          panelType: "PANEL",
          property: "ALL",
          scale: "ALL",
          sortByRank: false,
          status: "ACTIVE",
          system: "ALL",
          term: value, // user input
          timing: "ALL",
        }
      );

      const results = res.data.map((item) => ({
        panel_name:
          item.DISPLAY_NAME ||
          item.LONG_COMMON_NAME ||
          item.COMPONENT ||
          item.LOINC_NUMBER,
        loinc_num: item.LOINC_NUMBER,
      }));

      setSearchResults(results);
    } catch (err) {
      console.error("Error fetching panels:", err);
    }
  };

  // 🔹 debounce search (wait 1s after typing stops)
  useEffect(() => {
    if (!searchText) return;
    const timeout = setTimeout(() => {
      handlePanelSearch(searchText);
    }, 1000);
    return () => clearTimeout(timeout);
  }, [searchText]);

  // 🔹 fetch tests of a selected panel using lookup
  // const handlePanelSelect = async (panel) => {
  //   if (!panel) return;
  //   setSelectedPanel(panel.panel_name);

  //   if (panelEntries.some((p) => p.panel_name === panel.panel_name)) return;

  //   try {
  //     const res = await axios.get(
  //       `http://3.6.121.41:8081/loincserv/v1/lookup`,
  //       { params: { loincNumber: panel.loinc_num } }
  //     );

  //     const formattedTests = flattenTests(res.data, panel.panel_name);

  //     const updated = [
  //       ...panelEntries,
  //       {
  //         panel_name: panel.panel_name,
  //         tests: formattedTests,
  //       },
  //     ];

  //     setData(updated);
  //     setExpandedPanel(panel.panel_name);
  //   } catch (err) {
  //     console.error("Error fetching panel tests:", err);
  //   }
  // };

  const handlePanelSelect = async (panel) => {
    if (!panel) return;
    setSelectedPanel(panel.panel_name);

    if (panelEntries.some((p) => p.panel_name === panel.panel_name)) return;

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_ROUTE_URL}loinc/lookup/`,
        {
          loincNumber: panel.loinc_num, // request body
        }
      );

      const formattedTests = flattenTests(res.data, panel.panel_name);

      const updated = [
        ...panelEntries,
        {
          panel_name: panel.panel_name,
          tests: formattedTests,
        },
      ];

      setData(updated);
      setExpandedPanel(panel.panel_name);
    } catch (err) {
      console.error("Error fetching panel tests:", err);
    }
  };

  const handleTestChange = (panelIndex, testId, field, newValue) => {
    const updatedPanels = [...panelEntries];
    updatedPanels[panelIndex].tests = updatedPanels[panelIndex].tests.map(
      (row) => (row.id === testId ? { ...row, [field]: newValue } : row)
    );
    setData(updatedPanels);
  };

  const handleDeleteTest = (panelIndex, testId) => {
    const updatedPanels = [...panelEntries];
    updatedPanels[panelIndex].tests = updatedPanels[panelIndex].tests.filter(
      (row) => row.id !== testId
    );
    setData(updatedPanels);
  };

  const handleDeletePanel = (panelName) => {
    const updated = panelEntries.filter((p) => p.panel_name !== panelName);
    setData(updated);
    if (expandedPanel === panelName) setExpandedPanel(null);
  };

  return (
    <ThemeProvider theme={theme}>
      <Paper sx={{ padding: 3, marginTop: 3 }}>
        <Typography variant="h6" gutterBottom>
          Initial Observation
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <StyledAutocomplete
              fullWidth
              options={searchResults}
              getOptionLabel={(opt) => opt?.panel_name || ""}
              value={
                searchResults.find((p) => p.panel_name === selectedPanel) ||
                null
              }
              onInputChange={(e, value) => setSearchText(value)}
              onChange={(e, newVal) => handlePanelSelect(newVal)}
              renderInput={(params) => (
                <StyledTextField {...params} label="Search Panel" />
              )}
            />
          </Grid>
        </Grid>

        {/* Panels with tests */}
        {panelEntries.map((panel, panelIndex) => (
          <Accordion
            key={panel.panel_name}
            expanded={expandedPanel === panel.panel_name}
            onChange={() =>
              setExpandedPanel((prev) =>
                prev === panel.panel_name ? null : panel.panel_name
              )
            }
            sx={{ mt: 2, border: "1px solid #21cdc0", borderRadius: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="subtitle1">{panel.panel_name}</Typography>
                <Tooltip title="Delete Panel">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeletePanel(panel.panel_name);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer
                component={Paper}
                sx={{ border: "1px solid #ccc" }}
              >
                <Table>
                  <TableHead>
                    <StyledTableRowHistory>
                      <StyledTableCellHistory>Test Name</StyledTableCellHistory>
                      <StyledTableCellHistory>
                        LOINC Code
                      </StyledTableCellHistory>
                      {/* <StyledTableCellHistory>Description</StyledTableCellHistory> */}
                      <StyledTableCellHistory>Value</StyledTableCellHistory>
                      <StyledTableCellHistory>Unit</StyledTableCellHistory>
                      <StyledTableCellHistory>Actions</StyledTableCellHistory>
                    </StyledTableRowHistory>
                  </TableHead>
                  <TableBody>
                    {panel.tests.map((testRow) => (
                      <StyledTableRowHistory key={testRow.id}>
                        <StyledTableCellHistory>
                          {testRow.test}
                        </StyledTableCellHistory>
                        <StyledTableCellHistory>
                          {testRow.code}
                        </StyledTableCellHistory>
                        {/* <StyledTableCellHistory>{testRow.description}</StyledTableCellHistory> */}
                        <StyledTableCellHistory>
                          <StyledTextField
                            value={testRow.value}
                            size="small"
                            onChange={(e) =>
                              handleTestChange(
                                panelIndex,
                                testRow.id,
                                "value",
                                e.target.value
                              )
                            }
                          />
                        </StyledTableCellHistory>
                        <StyledTableCellHistory>
                          {/* <StyledSelect
                            size="small"
                            value={testRow.unit}
                            displayEmpty
                            onChange={(e) =>
                              handleTestChange(panelIndex, testRow.id, "unit", e.target.value)
                            }
                          >
                            <MenuItem value="">
                              <em>Select Unit</em>
                            </MenuItem>
                            {[...new Set([testRow.unit, ...unitOptions])].map(
                              (unit) => (
                                <MenuItem key={unit} value={unit}>
                                  {unit}
                                </MenuItem>
                              )
                            )}
                          </StyledSelect> */}
                          <StyledSelect
                            size="small"
                            value={testRow.unit}
                            displayEmpty
                            onChange={(e) =>
                              handleTestChange(
                                panelIndex,
                                testRow.id,
                                "unit",
                                e.target.value
                              )
                            }
                            sx={{
                              minWidth: 120, // fixed width
                              maxWidth: 150, // optional
                              height: 40, // fixed height
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              textOverflow: "ellipsis",
                            }}
                          >
                            <MenuItem value="">
                              <em>Select Unit</em>
                            </MenuItem>
                            {[...new Set([testRow.unit, ...unitOptions])].map(
                              (unit) => (
                                <MenuItem key={unit} value={unit}>
                                  {unit}
                                </MenuItem>
                              )
                            )}
                          </StyledSelect>
                        </StyledTableCellHistory>
                        <StyledTableCellHistory>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleDeleteTest(panelIndex, testRow.id)
                              }
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </StyledTableCellHistory>
                      </StyledTableRowHistory>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        ))}
      </Paper>
    </ThemeProvider>
  );
};

export default LoincAccordionPanelInput;
